import { Alert } from "PFComponents/alert";
import { Typography } from "PFComponents/typography";
import { removeLoadingState } from "PFCore/initializers/remove_loading_state";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./mcp_callback.module.scss";

// Delay before auto-closing on success — gives the user a moment to read
// the confirmation message. Not applicable when the tab was opened directly
// (no window.opener), where window.close() is blocked by the browser anyway.
const AUTO_CLOSE_DELAY_MS = 1500;

type CallbackState = { status: "success" } | { status: "error"; errorMessage: string };

const parseCallbackState = (
  search: string,
  messages: { missingCode: string; providerError: (details: string) => string }
): CallbackState => {
  const params = new URLSearchParams(search);

  // OAuth error responses take precedence: the provider has explicitly told us
  // the flow failed, so we must never render success even if a `code` is also
  // present. See RFC 6749 §4.1.2.1.
  const error = params.get("error");
  if (error) {
    const description = params.get("error_description");
    return {
      status: "error",
      errorMessage: messages.providerError(description || error)
    };
  }

  const code = params.get("code");
  if (!code) {
    return { status: "error", errorMessage: messages.missingCode };
  }

  return { status: "success" };
};

export const McpCallback = () => {
  const { t } = useTranslation("mcp", { keyPrefix: "callback" });

  const [state] = useState<CallbackState>(() =>
    parseCallbackState(window.location.search, {
      missingCode: t("error.missingCode"),
      providerError: (details: string) => t("error.providerError", { details })
    })
  );

  useEffect(() => {
    // The route bypasses <Bootstrapper />, which normally removes the initial
    // loader. Do it here so the page becomes visible.
    removeLoadingState();
  }, []);

  useEffect(() => {
    if (state.status !== "success") {
      return;
    }

    // Scrub the authorization code from the URL bar and history — the MCP
    // client has already consumed it from its own redirect.
    window.history.replaceState({}, document.title, window.location.pathname);

    const timeoutId = window.setTimeout(() => window.close(), AUTO_CLOSE_DELAY_MS);
    return () => window.clearTimeout(timeoutId);
  }, [state.status]);

  return (
    <div className={css.container}>
      <div className={css.card}>
        {state.status === "success" ? (
          <Alert kind="success" qaId="McpCallback.success" title={t("success.title")}>
            <Typography variant="bodyRegular" tag="p">
              {t("success.description")}
            </Typography>
          </Alert>
        ) : (
          <Alert kind="error" qaId="McpCallback.error" title={t("error.title")}>
            <Typography variant="bodyRegular" tag="p">
              {state.errorMessage}
            </Typography>
          </Alert>
        )}
      </div>
    </div>
  );
};
